import React from 'react'

import type {
  BinaryFilter,
  Filter,
  LogicalAndFilter,
  UnaryFilter,
} from '@cubejs-client/core'
import {
  CubeFilterDimensions,
  getFilterValuesFromKeyValueFilters,
  HumanizedCubeFilterDimensions,
  isCubeFilterKeyValueType,
  KeyValueFilterDict,
  useCubeReportFilterableEntities,
} from 'cube/components/CubeReportFilterRow'
import objectHash from 'object-hash'
import { equals, head, last } from 'ramda'

import { DismissableChip, DismissableChipGroup, Text } from '@driftt/dds'

import { ReportPage } from 'types/pages'

import {
  FilterChipGroupProps,
  FilterChipStore,
  HumanizedFilterWithHash,
} from './FilterChipGroup.types'

const isHourOfDayFilter = (member: string) =>
  equals(
    HumanizedCubeFilterDimensions[CubeFilterDimensions.HOUR_OF_DAY],
    member
  )

const getRenderableValueForFilter = (member: string, values: string[]) => {
  if (isHourOfDayFilter(member)) {
    return `${head(values)} - ${last(values)}`
  }
  return values.join(', ')
}

const FilterChip = ({ member, operator, values, filter, hashKey }) => {
  const { isKeyValue } = isCubeFilterKeyValueType(filter)

  if (isKeyValue) {
    const { key, value } = KeyValueFilterDict[member.value]

    const { key: selectedKeyValues, value: selectedValueValues } =
      getFilterValuesFromKeyValueFilters(
        filter as LogicalAndFilter,
        member.value
      )

    return (
      <DismissableChip key={hashKey} value={hashKey}>
        {member.label}{' '}
        <Text inline variant="bodyBold">
          {getRenderableValueForFilter(key, selectedKeyValues)}
        </Text>
        <Text inline variant="bodyBold">
          {` = `}
        </Text>
        <Text inline variant="bodyBold">
          {getRenderableValueForFilter(value, selectedValueValues)}
        </Text>
      </DismissableChip>
    )
  }

  return (
    <DismissableChip key={hashKey} value={hashKey}>
      {member.label} {operator}{' '}
      <Text inline variant="bodyBold">
        {getRenderableValueForFilter(member.label, values)}
      </Text>
    </DismissableChip>
  )
}

export const FilterChipGroup = <TData,>({
  useStore,
}: FilterChipGroupProps<TData>) => {
  const {
    store: { filters },
    updateStore,
  } = useStore()

  const { getHumanizedEntityByFilter } = useCubeReportFilterableEntities(
    ReportPage.ChatDetail
  )

  const renderableFilterChips = filters
    .map((filter) => {
      return {
        ...getHumanizedEntityByFilter(filter as BinaryFilter | UnaryFilter),
        filter: filter as Filter,
        hashKey: objectHash(filter, { respectType: false }),
      }
    })
    .filter((filter) => filter !== null) as HumanizedFilterWithHash[]

  const onChipDismissed = (values: string[] = []) => {
    const updatedFilters = filters.filter((filter) =>
      values.includes(objectHash(filter, { respectType: false }))
    )

    updateStore({
      filters: updatedFilters,
    } as FilterChipStore<TData>)
  }

  if (!filters.length) {
    return null
  }

  return (
    <DismissableChipGroup onChange={onChipDismissed}>
      {renderableFilterChips.map(FilterChip)}
    </DismissableChipGroup>
  )
}
